import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { SETTINGS_REGISTRY } from './org-settings.js';
import { pagedQuery, pagedResponse, PickerIdsSchema } from './pagination.js';
import { MessageSchema, MoneyStringSchema } from './primitives.js';
// The per-component capture shape is the document screens' (OQ-05-17). A till
// line that is an auto-deducting kit needs exactly the same blocks, because
// BR-MD-03-17 is a rule about the COMPONENT ITEM, not about which screen the
// sale was rung up on.
import { ComponentTrackingSchema } from './sales-invoices.js';
const c = initContract();
/**
 * SAL-06 — POINT OF SALE.
 *
 * The till is a standalone full-screen application with its own route and no
 * dashboard shell, and its API mirrors that: everything a counter does in one
 * action is ONE call. `completeSale` is the whole of BR-SAL-06-03 — "as one
 * action that either wholly succeeds or wholly fails, create the sale record,
 * deduct each line from the register's warehouse, record the payments and the
 * change, and write the journal entry".
 *
 * Every message below is quoted from §14 word for word, because the definition
 * of done requires it: "Every message in §14 appears word for word."
 */
/**
 * D12's number, and ONLY its registry default — the enforcing read is the
 * company setting `pos.refund_approval_threshold`, taken fresh per request in
 * `PosService.refund` so a threshold changed on ADM-01 moves the very next
 * refund. This constant exists for the two things a default is good for: the
 * message's own fallback argument, and the web's exact-match map entry, which
 * localizes the sentence a company that never touched the number will see.
 */
export const POS_REFUND_APPROVAL_THRESHOLD = SETTINGS_REGISTRY['pos.refund_approval_threshold']
    .default;
/**
 * The threshold as the refusal names it: grouped, and ALWAYS to two places.
 *
 *   '200.00' → '200.00'   '200' → '200.00'   '21.27' → '21.27'
 *   '1500.5' → '1,500.50' '25000.00' → '25,000.00'
 *
 * ── WHY NOT `formatApprovalLimit` FROM purchase-orders.ts ─────────────────
 *
 * That one DROPS the fraction ('25000.00' → '25,000'), which is right there
 * and wrong here. BR-PUR-01-01's sentence is quoted from the spec word for
 * word and the spec wrote a round number into it. This sentence is new prose,
 * and a shop is far likelier to set a refund ceiling with cents in it than a
 * 25,000 purchase limit — a company that sets 21.27 must not be told its
 * limit is "21", because a refusal that misreports the number it enforces
 * sends the reader to argue with a figure nobody chose. Money is shown to two
 * places everywhere else in this product (Money.toFixed2, the `Amount`
 * component) and this agrees with that.
 *
 * ── WHY THE PADDING IS LOAD-BEARING AND NOT TIDINESS ──────────────────────
 *
 * MEASURED, not assumed: a money setting stored as the JSON string "200.00"
 * comes back from `SettingsReader` as **'200'**. The jsonb round-trip parses
 * a numeric-looking string cell into a NUMBER and `coerceStoredSettingValue`
 * turns it back with `String(200)`, which cannot know it used to have a
 * fraction (see the comment on that function — the coercion is what stops the
 * value failing validation and falling back to the default, and losing the
 * trailing zeros is its cost). Non-zero cents survive: '21.27' round-trips
 * intact. Only trailing zeros are lost.
 *
 * That matters here for a reason beyond looks. The web localizes this
 * sentence by EXACT MATCH on the default-threshold text, and the default
 * reaches the formatter by two different roads — from the registry ('200.00')
 * when the message's default argument is used to build the map key, and from
 * settings ('200') when the server actually raises it. Without the padding
 * those two roads produce two different sentences, the map misses, and the
 * Arabic cashier meets English on the one path a default-configured company
 * will ever take. Padding makes the two converge, which is exactly why
 * `formatApprovalLimit`'s fraction-dropping works for the purchase order:
 * '25000.00' and '25000' collapse to the same string there too. Same
 * convergence, reached from the other end.
 */
export function formatRefundThreshold(threshold) {
    const [integer = '0', fraction = ''] = threshold.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${grouped}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}
export const POS_MESSAGES = {
    /** ERR-SAL-06-01 — the gate is structural; this is for a direct API call. */
    noOpenShift: 'Open the shift before selling.',
    /** Not from the spec: the first-register route above has to say something. */
    registerCodeTaken: 'A register with this code already exists.',
    noWarehouse: 'Create a warehouse before setting up a register.',
    /** ERR-SAL-06-02 */
    emptyCart: 'Cart is empty — tap products to add',
    /** ERR-SAL-06-03 */
    tenderedShort: 'Amount tendered is less than total',
    /** ERR-SAL-06-04 / ERR-SAL-06-05 — an empty search, and an empty category. */
    noProducts: 'No products match',
    /** ERR-SAL-06-08 — the count is interpolated by the caller. */
    parkedSalesOpen: (count) => `${count} sales are on hold. Finish or void them before closing the shift.`,
    /** ERR-SAL-06-09 — "The drawer is 4.00 short. Type an explanation before closing." */
    explainDiscrepancy: (short) => `The drawer is ${short} short. Type an explanation before closing.`,
    /** ERR-SAL-06-10 — BR-SAL-06-15's refusal, naming the item. */
    outOfStock: (item) => `There are no ${item} left in this shop.`,
    /** BR-SAL-06-09's cap. */
    refundExceedsSold: 'A returned quantity cannot exceed the quantity sold.',
    /** BR-SAL-06-18. */
    saleNotEditable: 'A completed sale is not editable. A mistake is fixed by a refund.',
    shiftAlreadyOpen: 'This register already has an open shift.',
    /**
     * NOT FROM THE SPEC — this is the build's own coinage, verified rather than
     * assumed: the sentence appears nowhere in `PalAccount System Documentation
     * .md`, measured with a positive control ("Cart is empty — tap products to
     * add" is found at spec 10450 and 10675 by the same search, so the search
     * reads POS sentences correctly and the absence is real).
     *
     * It used to read "No such sale." — 13 characters — and that length was not
     * merely terse, it was DISQUALIFYING. `contract-sentence-render.test.ts`
     * filters mapped entries through `isSentence`, which requires more than 15
     * characters, so an entry for the old string would have turned that guard red
     * and the sentence could never be localized at all. An Arabic cashier was
     * therefore structurally guaranteed to meet English here.
     *
     * Lengthened to a real refusal in SAL-06's register (PART 16.7: say what the
     * reader does next) so that it can carry a map entry. The wording claims
     * nothing about scope: the lookup is by sale id within the tenant and is NOT
     * register-scoped, so this does not say "on this till".
     */
    saleNotFound: 'No sale with that number exists. Refresh the list and pick it again.',
    /**
     * The refund path's OTHER not-found, which shared the sentence above and
     * should not have. `pos.service.ts` raises this when a requested
     * `saleLineId` is not among the original sale's lines — the sale was found,
     * one of its lines was not — so a sentence naming the SALE is false here.
     * Splitting them is what makes lengthening the one above safe; leaving them
     * merged would have turned a vague sentence into a specific wrong one.
     */
    saleLineNotFound: 'One of the lines you are refunding is not on this sale.',
    /**
     * A21, and the one boundary of the drawer arithmetic that cannot be posted.
     *
     * The deposit is COUNTED CASH OVER THE FLOAT (the ruling's own words), and
     * the float is not on the books — opening a shift stores `openingCash` and
     * posts nothing, so 1111 is only ever debited by a cash TENDER. When the
     * count comes in BELOW the float, "counted over float" is a negative
     * number, and a negative debit is not a thing a journal line can express.
     *
     * The tempting alternatives were both worse. Flooring the deposit at zero
     * makes the amount in 1119 stop equalling the difference the cashier was
     * shown and made to explain, so the screen and the books would disagree
     * about the same event. Writing it as a CREDIT to the bank says money went
     * from the bank into the drawer, which did not happen. So the close refuses
     * and a person decides — a drawer counted below its own float is not a
     * rounding error.
     */
    nothingToBank: 'The counted cash is less than the starting cash, so there is nothing to bank.',
    /**
     * A21 — `treasury.deposit_account` is blank AND the chart has no postable,
     * active cash leaf other than the till to fall back on. Both halves have to
     * fail: the ruling's default is "the first postable bank leaf", so an empty
     * setting is ordinary and this sentence is reached only by a chart that has
     * been edited down past the point where a deposit has anywhere to go. It
     * names the remedy because the remedy is not obvious from where it appears.
     */
    noDepositAccount: 'No account is set for the drawer deposit, and the chart has no cash account to bank into. Set one in General Settings.',
    /**
     * DELEGATED RULING D12 — the refusal a cashier meets on a refund above the
     * company's `pos.refund_approval_threshold`.
     *
     * NOT FROM §14, and it must not pretend to be. §14 has no row for this,
     * because the prototype checked nothing at all: §15's last edge case is
     * "No permission. Nothing is checked. A cashier and a supervisor have
     * identical powers." The sentence is therefore built to PART 16.7's own
     * template for this exact situation — "the action is blocked by a rule or a
     * limit, not a permission → show the button live and explain on click:
     * 'This order is over $25,000 and needs approval. Ask an administrator.'" —
     * with SAL-06's nouns. §5h and §9 call the second person a SUPERVISOR
     * ("Shift supervisor | Handles refunds, counts the drawer"), so the sentence
     * does too; "administrator" would send a cashier to the wrong desk.
     *
     * NO CURRENCY CODE, deliberately, where the PUR-01 sentence carries "USD".
     * That one is quoting BR-PUR-01-01 word for word and the spec wrote the code
     * into it. This one is new prose, and D29 puts the till in base currency
     * whatever base is — naming a code here would be a claim about the tenant's
     * base that this module has no business making.
     *
     * Parameterised, so the web's exact-match map localizes it AT THE DEFAULT
     * threshold and a company that changed the number gets the English sentence
     * through — the documented policy, and the same one PO_MESSAGES
     * .approvalRequired runs on (apps/web/src/lib/server-messages.ts).
     */
    refundNeedsApproval: (threshold = POS_REFUND_APPROVAL_THRESHOLD) => `This refund is over the ${formatRefundThreshold(threshold)} approval limit. Ask a supervisor to process it.`,
};
/**
 * D46 — SAL-06's CONVERTED parameterised refusals, structured. These four are
 * the till's live-value sentences the exact-match map structurally could not
 * hold (`parkedSalesOpen`, `explainDiscrepancy`, `outOfStock`) plus the one
 * it held ONLY at its default (`refundNeedsApproval` — a company that changed
 * `pos.refund_approval_threshold` got English; the retiring
 * `pos.approvalLimitRefusal` entry was that brittleness). Money params are
 * PRE-FORMATTED with the sentences' own formatter so the ICU value stays a
 * plain placeholder. Sentence parity is pinned by `refusal-census.spec.ts`.
 */
export const POS_REFUSALS = {
    parkedSalesOpen: (count) => ({
        message: POS_MESSAGES.parkedSalesOpen(count),
        key: 'pos.errParkedSalesOpen',
        params: { count },
    }),
    explainDiscrepancy: (short) => ({
        message: POS_MESSAGES.explainDiscrepancy(short),
        key: 'pos.errExplainDiscrepancy',
        params: { short },
    }),
    outOfStock: (item) => ({
        message: POS_MESSAGES.outOfStock(item),
        key: 'pos.errOutOfStock',
        params: { item },
    }),
    refundNeedsApproval: (threshold = POS_REFUND_APPROVAL_THRESHOLD) => ({
        message: POS_MESSAGES.refundNeedsApproval(threshold),
        key: 'pos.errRefundNeedsApproval',
        params: { threshold: formatRefundThreshold(threshold) },
    }),
};
// ── The shift ───────────────────────────────────────────────────────────────
export const PosShiftSchema = z
    .object({
    id: z.uuid(),
    registerId: z.uuid(),
    registerCode: z.string(),
    registerName: z.string(),
    warehouseId: z.uuid(),
    status: z.enum(['open', 'closed']),
    openedAt: z.string(),
    openingCash: MoneyStringSchema,
    /** Cash tenders taken this shift — what the drawer should have gained. */
    cashSales: MoneyStringSchema,
    /** openingCash + cashSales (§5h). */
    expectedCash: MoneyStringSchema,
    countedCash: MoneyStringSchema.nullable(),
    discrepancy: MoneyStringSchema.nullable(),
    discrepancyReason: z.string().nullable(),
    /**
     * A21 — the "Drawer banked at close" entry (SAL-06 §12). Null on an open
     * shift always, and on a closed shift that had no money to move: no cash
     * sales and a count equal to the float writes no entry, because an entry
     * with no lines is not a document.
     */
    depositJournalEntryId: z.uuid().nullable(),
    /** BR-SAL-06-11 refuses a close while any of these remain. */
    parkedCount: z.number().int(),
    salesCount: z.number().int(),
})
    .strict();
// ── The cart and the sale ───────────────────────────────────────────────────
/**
 * A kit's part as the till needs it: what to ask the cashier for, and how
 * much of it one kit takes. `baseQty` is already in the COMPONENT's stock
 * unit (BR-MD-03-18), so the till multiplies by the cart quantity and nothing
 * has to re-interpret a unit at the counter.
 */
export const PosKitComponentSchema = z
    .object({
    componentItemId: z.uuid(),
    componentName: z.string(),
    tracking: z.enum(['none', 'serial', 'batch']),
    baseQty: z.string(),
})
    .strict();
export const CartLineSchema = z
    .object({
    itemId: z.uuid(),
    qty: z.string().regex(/^\d+(\.\d{1,6})?$/),
    unitPrice: MoneyStringSchema,
    /** §5h: an amount, never a percentage. */
    lineDiscount: MoneyStringSchema.optional(),
    /**
     * BR-MD-03-17 at the counter. A serial-tracked item names one identifier
     * per unit and a batch-tracked one names its lot — the same requirement
     * the document screens meet, met here by SCANNING rather than typing into
     * a grid (SAL-06 §5c: the scan box is how a till takes an identifier).
     */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
    /** R7: an auto-deducting kit's per-component capture. */
    components: ComponentTrackingSchema.array().max(200).optional(),
})
    .strict();
/**
 * What a completed line actually took off the shelf, read back from the
 * movements it wrote — never from a snapshot, because the movements ARE the
 * record of which unit left.
 *
 * One block per moved item: the line's own item, or one per component when
 * the line was an auto-deducting kit. It is what lets the returns screen ask
 * "which one is coming back?" with the real answers on screen instead of an
 * empty box, and what stops a returned unit rejoining the shelf unnamed.
 */
export const PosLineTrackingSchema = z
    .object({
    /** null = the line's own item; otherwise the kit component that moved. */
    componentItemId: z.uuid().nullable(),
    itemName: z.string(),
    tracking: z.enum(['serial', 'batch']),
    serialNos: z.string().array(),
    batchNo: z.string().nullable(),
})
    .strict();
export const PosSaleLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    itemName: z.string(),
    qty: z.string(),
    unitPrice: MoneyStringSchema,
    lineDiscount: MoneyStringSchema,
    lineNet: MoneyStringSchema,
    /** How much of this line has already been refunded (BR-SAL-06-09's cap). */
    refundedQty: z.string(),
    /** Empty for anything untracked — most lines at most tills. */
    tracking: z.array(PosLineTrackingSchema),
})
    .strict();
export const PosSaleSchema = z
    .object({
    id: z.uuid(),
    /** POS-#### — 1000 plus the count of completed sales plus one (§5g). */
    saleNo: z.string(),
    soldAt: z.string(),
    /** Null IS the walk-in customer; every sale still has a party (§5e). */
    partnerId: z.uuid().nullable(),
    partnerName: z.string().nullable(),
    subtotal: MoneyStringSchema,
    cartDiscount: MoneyStringSchema,
    taxAmount: MoneyStringSchema,
    total: MoneyStringSchema,
    changeGiven: MoneyStringSchema,
    /** 'refund' is the refund document; 'refunded' is an original that has one. */
    status: z.enum(['completed', 'refund', 'refunded', 'voided']),
    madeOffline: z.boolean(),
    lines: z.array(PosSaleLineSchema),
    payments: z.array(z.object({ method: z.enum(['cash', 'card']), amount: MoneyStringSchema }).strict()),
    /** What the ledger wrote. A pointer, so the till never restates accounting. */
    journalEntryId: z.uuid().nullable(),
})
    .strict();
/**
 * A payment is a LIST, so a split is simply two entries — which is why
 * BR-SAL-06-17's two accounts (1110 cash, 1112 card) need no special case in
 * the entry builder. §15: "A split payment must still cover the whole total."
 */
export const CompleteSaleSchema = z
    .object({
    lines: z.array(CartLineSchema).min(1),
    cartDiscount: MoneyStringSchema.optional(),
    partnerId: z.uuid().optional(),
    payments: z
        .array(z
        .object({
        method: z.enum(['cash', 'card']),
        amount: MoneyStringSchema,
        /** Cash only: what was handed over, before change. */
        tendered: MoneyStringSchema.optional(),
    })
        .strict())
        .min(1),
    /** BR-SAL-06-13: each sale records whether it was made offline. */
    madeOffline: z.boolean().optional(),
    /** Resuming a parked cart completes and clears it in one step. */
    parkedCartId: z.uuid().optional(),
})
    .strict();
/** BR-SAL-06-10 — what the prototype does not do and must. */
export const RefundSchema = z
    .object({
    saleId: z.uuid(),
    lines: z
        .array(z
        .object({
        saleLineId: z.uuid(),
        qty: z.string(),
        /**
         * BR-MD-03-17 does not point one way: a tracked unit that comes
         * back must say WHICH unit, or it rejoins the shelf as an
         * anonymous +1 and can never be sold again.
         */
        serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
        batchNo: z.string().trim().min(1).max(40).optional(),
        /** R7 in reverse: a kit came back as its parts. */
        components: ComponentTrackingSchema.array().max(200).optional(),
    })
        .strict())
        .min(1),
    /** Whether the goods go back into sellable stock (§5i). */
    restock: z.boolean(),
    method: z.enum(['cash', 'card']),
})
    .strict();
/**
 * A parked cart READS with the product name on every line. The input shape
 * deliberately has none — a till posts item ids — but a resumed cart has to
 * put words back on the screen, and the catalogue is the authority on what
 * those words are. Resolving at read time rather than snapshotting at park
 * time also means a name corrected in the master shows up correctly on a cart
 * that was parked before the correction.
 */
export const ParkedLineSchema = CartLineSchema.extend({
    name: z.string(),
    /**
     * And the same argument for the tracking facts: a resumed cart has to know
     * that its wrench wants a serial, and looking that up on the screen would
     * mean searching the catalogue for a line the cashier is not searching for.
     */
    tracking: z.enum(['none', 'serial', 'batch']),
    autoDeduct: z.boolean(),
    /**
     * Named apart from the line's own `components` on purpose: those are what
     * was CAPTURED for each part, these are what the parts ARE.
     */
    kitComponents: z.array(PosKitComponentSchema),
}).strict();
export const ParkedCartSchema = z
    .object({
    id: z.uuid(),
    label: z.string(),
    parkedAt: z.string(),
    lineCount: z.number().int(),
    total: MoneyStringSchema,
    partnerId: z.uuid().nullable(),
    lines: z.array(ParkedLineSchema),
})
    .strict();
/** The catalogue as the till reads it — barcode first, then code, then name. */
export const PosProductSchema = z
    .object({
    itemId: z.uuid(),
    code: z.string(),
    name: z.string(),
    barcode: z.string().nullable(),
    category: z.string().nullable(),
    price: MoneyStringSchema,
    onHand: z.string(),
    /** MD-03 panel 7. 'none' is the ordinary counter item. */
    tracking: z.enum(['none', 'serial', 'batch']),
    /**
     * MD-03 field 7 — "this one switch decides what a kit is". True means the
     * till must resolve the tile into the kit's PARTS: their stock, their
     * serials, their batches. False (including every non-kit) means the tile
     * is the stocked thing itself.
     */
    autoDeduct: z.boolean(),
    /** Empty unless autoDeduct — the parts one tap of this tile consumes. */
    components: z.array(PosKitComponentSchema),
})
    .strict();
export const posContract = c.router({
    /** The register list and whichever shift is open on it. */
    session: {
        method: 'GET',
        path: '/api/pos/session',
        query: z.object({ registerId: z.uuid().optional() }),
        responses: {
            200: z
                .object({
                registers: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string(), warehouseId: z.uuid() }).strict()),
                shift: PosShiftSchema.nullable(),
                /** BR-SAL-06-16: read from company settings, never hard-coded. */
                taxRate: z.string(),
            })
                .strict(),
        },
        summary: 'SAL-06 — the gate: which registers exist, and is a shift open (sales.view)',
    },
    /**
     * SAL-06 has no register-administration screen, and §11 says the till "is
     * created from nothing". Something still has to bring the FIRST register
     * into existence, or the whole screen is unreachable in a fresh company —
     * every register in the prototype arrived by hand in the database. This is
     * that one route, and it is the smallest thing that removes the dead end:
     * a code, a name, and the warehouse the till sells out of.
     */
    createRegister: {
        method: 'POST',
        path: '/api/pos/registers',
        body: z
            .object({
            code: z.string().trim().min(2).max(20),
            name: z.string().trim().min(2).max(120),
            warehouseId: z.uuid(),
        })
            .strict(),
        responses: {
            201: z
                .object({ id: z.uuid(), code: z.string(), name: z.string(), warehouseId: z.uuid() })
                .strict(),
            400: MessageSchema,
            409: MessageSchema,
        },
        summary: 'SAL-06 — the first register has to come from somewhere (sales.create)',
    },
    openShift: {
        method: 'POST',
        path: '/api/pos/shifts',
        body: z.object({ registerId: z.uuid(), openingCash: MoneyStringSchema }).strict(),
        responses: { 201: PosShiftSchema, 400: MessageSchema, 409: MessageSchema },
        summary: 'SAL-06 §5b — nothing sells until this succeeds (BR-SAL-06-02)',
    },
    closeShift: {
        method: 'POST',
        path: '/api/pos/shifts/:shiftId/close',
        pathParams: z.object({ shiftId: z.uuid() }),
        body: z
            .object({
            countedCash: MoneyStringSchema,
            /** BR-SAL-06-12: required whenever the discrepancy is not zero. */
            discrepancyReason: z.string().max(500).optional(),
        })
            .strict(),
        responses: { 200: PosShiftSchema, 400: MessageSchema, 409: MessageSchema },
        summary: 'SAL-06 §5h — counts the drawer; refuses on parked sales or an unexplained difference',
    },
    products: {
        method: 'GET',
        path: '/api/pos/products',
        query: pagedQuery({ registerId: z.uuid(), search: z.string().max(120).optional() }),
        responses: { 200: pagedResponse('products', PosProductSchema) },
        summary: 'SAL-06 §5c — barcode first, then exact code, then any part of the name. PAGED (NFR-42): this read carried a hard `LIMIT 200` in SQL, which at 50,000 items showed the cashier the first 200 by name and said nothing about the rest.',
    },
    completeSale: {
        method: 'POST',
        path: '/api/pos/shifts/:shiftId/sales',
        pathParams: z.object({ shiftId: z.uuid() }),
        body: CompleteSaleSchema,
        responses: { 201: PosSaleSchema, 400: MessageSchema, 409: MessageSchema },
        summary: 'SAL-06 §12 — ONE action: sale + stock out + payments + the balanced entry (BR-SAL-06-03)',
    },
    /**
     * SAL-06 §5i — the past sales the returns screen picks from, ONE PAGE at a
     * time (NFR-42).
     *
     * Measured at 2,000 sales in one shift — one busy retail day — this route
     * answered in **3.5 seconds and 1.5 MB**, which is the first list in this
     * product measured OVER PART 20.7 row 4's two-second ceiling rather than
     * merely large. The shift is not a bound: it is a day, and a day at a till is
     * however many customers walk in.
     *
     * Two query fields, and both existed as JavaScript on the screen:
     *
     *  - `refundable` is §5i's own rule — a refund is not itself refundable, so
     *    the returns list never offers one. It was `.filter((s) => s.status !==
     *    'refund')` in the browser, which a LIMIT in front of would have turned
     *    into a page that is short by however many refunds it contained.
     *  - `ids` is the picker convention (§7): the cashier has TAPPED a sale, and
     *    that sale must keep rendering when the list pages away from it. It
     *    ignores `refundable` for the same reason `ids` ignores `status`
     *    everywhere else — what a form is holding is not what a list offers.
     */
    sales: {
        method: 'GET',
        path: '/api/pos/shifts/:shiftId/sales',
        pathParams: z.object({ shiftId: z.uuid() }),
        query: pagedQuery({
            refundable: z.enum(['true']).optional(),
            ids: PickerIdsSchema.optional(),
        }),
        responses: { 200: pagedResponse('sales', PosSaleSchema) },
        summary: 'SAL-06 §5i — the past sales the returns screen picks from, one page at a time',
    },
    refund: {
        method: 'POST',
        path: '/api/pos/shifts/:shiftId/refunds',
        pathParams: z.object({ shiftId: z.uuid() }),
        body: RefundSchema,
        responses: { 201: PosSaleSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'SAL-06 §12 — credit note + reversing entry + restock (BR-SAL-06-10)',
    },
    parkCart: {
        method: 'POST',
        path: '/api/pos/shifts/:shiftId/parked',
        pathParams: z.object({ shiftId: z.uuid() }),
        body: z
            .object({ label: z.string().min(1).max(60), lines: z.array(CartLineSchema).min(1), partnerId: z.uuid().optional() })
            .strict(),
        responses: { 201: ParkedCartSchema, 400: MessageSchema },
        summary: 'SAL-06 §5d — a ROW, so it survives a reload (BR-SAL-06-14)',
    },
    parkedCarts: {
        method: 'GET',
        path: '/api/pos/shifts/:shiftId/parked',
        pathParams: z.object({ shiftId: z.uuid() }),
        responses: { 200: z.object({ carts: z.array(ParkedCartSchema) }).strict() },
        summary: 'SAL-06 §5d — the parked strip',
    },
    deleteParkedCart: {
        method: 'DELETE',
        path: '/api/pos/parked/:cartId',
        pathParams: z.object({ cartId: z.uuid() }),
        body: c.noBody(),
        responses: { 204: c.noBody(), 404: MessageSchema },
        summary: 'SAL-06 §5d — resuming or voiding a parked cart clears it',
    },
});
//# sourceMappingURL=pos.js.map